import { createLogger } from '@utils/logger'
import { AxiosError } from 'axios'
import { SerializableError, StandardFieldError, StandardFieldErrors } from '@app-types/general'

const log = createLogger('SerializableError')
log.setLevel('error')

// Helper function to check if the error is an AxiosError
const isAxiosError = (error: any): error is AxiosError => {
  return error && error.isAxiosError
}

// Helper function to check if an object has a message property
const hasMessage = (error: any): error is { message: string } => {
  return error && typeof error.message === 'string'
}

// Function to provide default error messages based on status codes
const getDefaultErrorMessage = (status: number | undefined): string => {
  switch (status) {
    case 401:
      return 'Unauthorized'
    case 403:
      return 'Forbidden'
    case 404:
      return 'Not Found'
    case 408:
      return 'Request Timeout'
    case 500:
      return 'Server Error'
    case 502:
      return 'Bad Gateway'
    case 503:
      return 'Server Unavailable'
    case 504:
      return 'Gateway Timeout'
    default:
      return 'An unknown error occurred'
  }
}

// Function to handle Axios errors specifically
const handleAxiosError = (error: AxiosError): SerializableError => {
  log.info(error)
  const result: SerializableError = {
    message: 'An unknown error occurred',
    status: error.response?.status,
  }

  if (!error.response) {
    result.message = error.message || 'Network Error: No response received from server'
    return result
  }

  const data = error.response.data

  // Initialize parsed data
  let parsed: any = null

  if (typeof data === 'string') {
    // Attempt to parse JSON string
    try {
      parsed = JSON.parse(data)
    } catch {
      log.error('Error parsing JSON from response:', data)
      result.message = error.message || 'Error parsing response data'
      return result
    }
  } else if (typeof data === 'object' && data !== null) {
    // If data is already an object, use it as-is
    parsed = data
  } else {
    // Handle other unexpected data types
    log.error('Unexpected response data type:', data)
    result.message = error.message || getDefaultErrorMessage(error.response.status)
    return result
  }

  // Extract message and field errors
  result.message = parsed.message || error.message || getDefaultErrorMessage(error.response.status)

  if (parsed.errors) {
    result.fieldErrors = extractFieldErrorsFromParsedErrors(parsed.errors)
  } else if (parsed.error) {
    result.message = parsed.error || parsed.message || 'Unknown error'
  }

  // Attach parsed data to the result
  result.data = parsed

  result.code = parsed.code || error.code || 'UNKNOWN_ERROR'

  // Ensure we have a message
  result.message = result.message || getDefaultErrorMessage(error.response.status)

  return result
}

// Main function to handle various error types
export const serializableError = (error: unknown): SerializableError => {
  let result: SerializableError = {
    message: 'An unknown error occurred',
  }

  // Handle string error
  if (typeof error === 'string') {
    result.message = error
    return result
  }

  // Handle Axios error
  if (isAxiosError(error)) {
    return handleAxiosError(error)
  }

  // Handle standard Error or objects with message key
  if (typeof error === 'object' && error !== null) {
    if (hasMessage(error)) {
      result.message = error.message || 'Unknown error'
      return result
    }
  }

  // Fallback for unknown error formats
  result.message = 'Unexpected error format'
  return result
}

// Helper to extract field-specific errors
export const getErrorsForField = (error: SerializableError | undefined, fieldName: string): string[] | undefined => {
  if (!error || !error.fieldErrors) {
    return undefined
  }

  return error.fieldErrors
    .filter((item: StandardFieldError) => item.field === fieldName)
    .map((item: StandardFieldError) => item.detail)
}

// Check if an error object looks like a SerializableError
export const looksLikeSerializableError = (error: unknown): error is SerializableError => {
  if (typeof error !== 'object' || error === null) {
    return false
  }

  const e = error as { [key: string]: any }

  // Check that 'message' exists and is a string
  if (typeof e.message !== 'string') {
    return false
  }

  // If 'status' exists, it should be a number
  if ('status' in e && typeof e.status !== 'number') {
    return false
  }

  // If 'fieldErrors' exists, it should be an array of objects (an array of objects)
  if ('fieldErrors' in e) {
    if (!Array.isArray(e.fieldErrors) || !e.fieldErrors.every(item => typeof item === 'object' && item !== null)) {
      return false
    }
  }

  // If 'code' exists, it should be a string
  if ('code' in e && typeof e.code !== 'string') {
    return false
  }

  // All checks passed
  return true
}

// Helper function to extract field errors from parsed errors
const extractFieldErrorsFromParsedErrors = (errors: any): StandardFieldErrors => {
  if (Array.isArray(errors)) {
    // Handle array of errors (JSON:API format)
    const fieldErrors = errors.map(({ source, detail, title }) => {
      const field = source?.attribute || extractFieldFromPointer(source?.pointer) || ''
      // Note: Don't decide on the message here, just use the raw message
      // This allows us to decide what the final message will be in the component itself.
      // Sometimes we need to replace the field name with a more user-friendly name.
      const msg = detail || title || 'Invalid attribute'
      return {
        field,
        detail: msg,
      }
    })
    return fieldErrors.length > 0 ? fieldErrors : undefined
  } else if (typeof errors === 'object' && errors !== null) {
    // Handle object of errors (Rails format)
    const fieldErrorsArray: StandardFieldError[] = []
    for (const field in errors) {
      const messages = errors[field]
      messages.forEach((msg: string) => {
        // Note: Don't decide on the message here, just use the raw message
        // This allows us to decide what the final message will be in the component itself.
        // Sometimes we need to replace the field name with a more user-friendly name.
        fieldErrorsArray.push({
          field,
          detail: msg,
        })
      })
    }
    return fieldErrorsArray.length > 0 ? fieldErrorsArray : undefined
  } else {
    // errors is neither array nor object
    return undefined
  }
}

// Helper function to extract field name from JSON:API pointer
const extractFieldFromPointer = (pointer?: string): string | null => {
  if (typeof pointer !== 'string') return null
  // Remove leading slash and split the pointer
  const parts = pointer.replace(/^\//, '').split('/')
  // Find 'attributes' and extract the field path
  const attributesIndex = parts.indexOf('attributes')
  if (attributesIndex !== -1 && parts.length > attributesIndex + 1) {
    const fieldParts = parts.slice(attributesIndex + 1)
    return fieldParts.join('.')
  }
  return null
}
